/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation, type NavigationProp, type ParamListBase } from '@react-navigation/native';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useSessionEmployeeRecord } from '../../auth/hooks/useSessionEmployeeRecord';
import { logout } from '../../auth/public-api';
import {
  resolvePostLoginRoute,
  type PostLoginHubMode,
} from '../../shell/session/routing/resolvePostLoginRoute';
import { navigateRoot } from '../../shell/navigation/rootNavigation';
import { ticketColors } from '../../shared/theme/ticketColors';
import { OwnerBranchesPanelScreen } from '../../owner-panel/screens/OwnerBranchesPanelScreen';
import { EmployeeBranchPickerHub } from './EmployeeBranchPickerHub';
import { PostLoginHubOwnerChrome } from '../components/PostLoginHubOwnerChrome';

function HubBody({ mode, navigation }: { mode: PostLoginHubMode; navigation: NavigationProp<ParamListBase> }) {
  if (mode === 'panel') {
    return (
      <OwnerBranchesPanelScreen
        entryTarget="post_login_hub"
        navigationOverride={navigation}
        testID="post-login-hub.panel"
      />
    );
  }
  return <EmployeeBranchPickerHub navigation={navigation} />;
}

export function PostLoginHubScreen() {
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const branches = useSessionStore((s) => s.branches);
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const { entitlements } = usePlanEntitlements();
  const employeeState = useSessionEmployeeRecord();
  const employeeRecord = employeeState.record;

  const isOwner = entitlements?.is_owner === true;
  const isEmployee = employeeRecord?.isEmployee === true;
  const isGrant = employeeRecord?.isGrant === true;
  const rolePending = !isOwner && employeeState.status === 'loading';
  const roleFailed = !isOwner && employeeState.status === 'error';

  const activeBranchCount = useMemo(
    () => branches.filter((b) => b.isActive !== false).length,
    [branches],
  );

  const route = useMemo(
    () =>
      resolvePostLoginRoute({
        activeBranchCount,
        isOwner,
        isEmployee,
        isGrant,
        currentBranchId,
      }),
    [activeBranchCount, currentBranchId, isEmployee, isGrant, isOwner],
  );

  useEffect(() => {
    if (rolePending || roleFailed) return;
    if (route.kind === 'app_shell') {
      navigateRoot(navigation, 'AppShell');
      return;
    }
    if (route.kind === 'branch_selector') {
      navigateRoot(navigation, 'BranchSelector');
    }
  }, [navigation, rolePending, roleFailed, route.kind]);

  // #1297: el resolver de rol falló (ej. bootstrap throweó) — antes quedaba
  // un spinner infinito sin escape. Estado de error con retry y logout.
  if (roleFailed) {
    return (
      <View style={styles.pending} testID="post-login-hub.error">
        <Text style={styles.errorTitle}>No pudimos cargar tu perfil</Text>
        <Text style={styles.errorBody}>
          Falló la conexión al preparar tu sesión. Probá de nuevo o volvé a entrar.
        </Text>
        <TouchableOpacity
          style={styles.retryBtn}
          onPress={employeeState.retry}
          accessibilityRole="button"
          accessibilityLabel="Reintentar"
          testID="post-login-hub.retry"
        >
          <Text style={styles.retryLabel}>Reintentar</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => void logout()}
          accessibilityRole="button"
          accessibilityLabel="Cerrar sesión"
          testID="post-login-hub.logout"
        >
          <Text style={styles.logoutLabel}>Cerrar sesión</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (rolePending || route.kind !== 'post_login_hub') {
    return (
      <View style={styles.pending} testID="post-login-hub.redirecting">
        <ActivityIndicator color={ticketColors.gold} />
      </View>
    );
  }

  return (
    <View style={styles.root}>
      {route.mode === 'panel' && isOwner ? (
        <PostLoginHubOwnerChrome navigation={navigation} branchCount={activeBranchCount} />
      ) : null}
      <HubBody mode={isOwner ? route.mode : 'picker'} navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  pending: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.bg,
    padding: 24,
    gap: 12,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: ticketColors.ink,
    textAlign: 'center',
  },
  errorBody: {
    fontSize: 14,
    color: ticketColors.ink2,
    textAlign: 'center',
    maxWidth: 320,
  },
  retryBtn: {
    marginTop: 8,
    backgroundColor: ticketColors.gold,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 32,
  },
  retryLabel: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.goldInk,
  },
  logoutLabel: {
    fontSize: 14,
    color: ticketColors.ink3,
    textDecorationLine: 'underline',
  },
});
